import { useEffect, useRef, useState } from 'react';
import { startAutocomplete } from './Autocomplete';
import {
  buildInstantSearch,
  getInstantSearchUiState,
  setInstantSearchUiState,
} from './InstantSearch';

export type SearchFormProps = {
  showFacets?: boolean;
  hasHits?: boolean;
  hasHelpCentre?: boolean;
  helpCentre?: boolean;
  focusOnMount?: boolean;
  resultsUrl?: string;
  emptyResultsMessage?: string;
};

const SearchForm: React.FC<SearchFormProps> = ({
  showFacets,
  hasHits,
  helpCentre,
  hasHelpCentre,
  focusOnMount,
  resultsUrl,
  emptyResultsMessage,
}) => {
  const panelRootRef = useRef(null);
  const rootRef = useRef(null);

  const params = new URL(window.location.href).searchParams;
  const [instantSearch, setInstantSearch] = useState(null);

  // if query string variable `within` is set t`help` then filter by help centre`
  const isWithinHelp = params.get('within') === 'help';

  const [filterByHelp, setFilterByHelp] = useState(
    hasHelpCentre && (helpCentre || isWithinHelp)
  );
  const [autocomplete, setAutocomplete] = useState(null);
  const autocompleteContainer = useRef(null);

  useEffect(() => {
    if (autocompleteContainer.current && !instantSearch) {
      const instantSearchInst = buildInstantSearch({
        hasHits,
        emptyResultsMessage,
      });

      const auto = startAutocomplete(
        autocompleteContainer.current,
        panelRootRef,
        rootRef,
        instantSearchInst,
        {
          hasHits,
          helpCentre,
          resultsUrl,
        }
      );

      setInstantSearch(instantSearchInst);
      setAutocomplete(auto);

      if (focusOnMount) {
        // focus the autocomplete input
        const input = autocompleteContainer.current.querySelector('input');

        if (input) {
          input.focus();
        }
      }

      // if query string variable has a value, start the search
      const query = params.get('q');

      if (query) {
        if (!instantSearchInst.started) {
          instantSearchInst.start();
        }
      }
    }

    return () => {
      if (autocomplete) {
        autocomplete.destroy();
      }

      setInstantSearch(null);
    };
  }, [autocompleteContainer.current]);

  return (
    <div className="search-form-container">
      <div className="search-form">
        <div id="autocomplete" ref={autocompleteContainer} />

        {showFacets && hasHelpCentre && (
          <div id="facets">
            <label>Search within:</label>

            <div id="facets-container">
              <button
                type="button"
                className={filterByHelp === false ? 'active' : ''}
                onClick={() => {
                  setFilterByHelp(false);

                  const { searchParams } = new URL(window.location.href);
                  searchParams.set('within', 'all');
                  // push state
                  window.history.pushState(
                    {},
                    '',
                    `${window.location.pathname}?${searchParams.toString()}`
                  );

                  // trigger new search
                  const state = getInstantSearchUiState();
                  setInstantSearchUiState(state);
                }}
              >
                Whole site
              </button>

              <button
                type="button"
                className={filterByHelp ? 'active' : ''}
                onClick={() => {
                  setFilterByHelp(true);

                  // set the query string variable `within` to `help`
                  const { searchParams } = new URL(window.location.href);
                  searchParams.set('within', 'help');

                  // push state
                  window.history.pushState(
                    {},
                    '',
                    `${window.location.pathname}?${searchParams.toString()}`
                  );

                  // trigger new search
                  const state = getInstantSearchUiState();
                  setInstantSearchUiState(state);
                }}
              >
                Help centre
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchForm;
